import { useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../notifications/hooks/useNotification';
import {
  coverage,
  terrainCoverageCount,
  descendants,
  normalize,
  toggleNode,
  toPayload,
  type PerimeterNode,
  type PerimeterTree,
  type TreeNode,
} from './perimeterTree.logic';

export interface PerimeterSelectorDrawerProps {
  open: boolean;
  onClose: () => void;
  tree: PerimeterTree;
  /** Currently applied selection (node ids). The drawer edits a local draft. */
  selectedIds: string[];
  onApply: (nodes: PerimeterNode[]) => void;
}

/** Accent/case-insensitive substring match. */
const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export function PerimeterSelectorDrawer({
  open,
  onClose,
  tree,
  selectedIds,
  onApply,
}: PerimeterSelectorDrawerProps) {
  // The temporary Drawer unmounts its content on close, so DrawerContent remounts
  // on every open and its draft re-snapshots `selectedIds` — a close/cancel can
  // never leak a change back to the parent, with no reset effect.
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <DrawerContent tree={tree} selectedIds={selectedIds} onApply={onApply} onClose={onClose} />
    </Drawer>
  );
}

function DrawerContent({
  tree,
  selectedIds,
  onApply,
  onClose,
}: Omit<PerimeterSelectorDrawerProps, 'open'>) {
  const { t } = useTranslation('users');
  const { notifySuccess, notifyError } = useNotification();

  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const covered = useMemo(() => coverage(draft, tree), [draft, tree]);
  const roots = useMemo(() => normalize(draft, tree), [draft, tree]);
  const countryCount = useMemo(
    () => tree.root.children.filter(node => node.type === 'pays').length,
    [tree],
  );
  const selectedCount = roots.includes('all') ? countryCount : roots.length;

  const toggle = (id: string) => setDraft(current => toggleNode(current, tree, id));
  const toggleCollapse = (id: string) =>
    setCollapsed(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => {
    if (roots.length === 0) {
      notifyError(t('perimeterSelector.emptyError'));
      return;
    }
    onApply(toPayload(draft, tree));
    // Nothing is persisted until the form is saved — make that clear in the toast.
    notifySuccess(t('perimeterSelector.applied'));
    onClose();
  };

  // The tree builder stores a French fallback for the root; localize it at render
  // (label, accessible name and search all use this).
  const displayLabel = (node: TreeNode) => (node.type === 'all' ? t('perimeter.all') : node.label);

  const matchesDeep = (node: TreeNode): boolean =>
    !query || norm(displayLabel(node)).includes(norm(query)) || node.children.some(matchesDeep);

  const renderNode = (node: TreeNode, depth: number): ReactNode => {
    if (!matchesDeep(node)) return null;

    const isRetained = roots.includes(node.id);
    const isCovered = covered.has(node.id);
    const isInherited = isCovered && !isRetained;
    const hasChildren = node.children.length > 0;
    const isOpen = Boolean(query) || !collapsed.has(node.id);
    // A collapsed parent with only some descendants covered shows an indeterminate box,
    // so a hidden selection underneath it is still visible.
    const isIndeterminate =
      !isCovered && hasChildren && descendants(tree, node.id).some(d => covered.has(d));
    const label = displayLabel(node);

    return (
      <Box key={node.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pl: depth * 2.5,
            py: 0.25,
            borderRadius: '6px',
            transition: 'background-color .12s ease',
            // Maquette tree rows (3 levels):
            //  .tn__r--on  retained/section → accent-light #E6F7FB + inner border #BFE6F1
            //  .tn__r--in  inherited/sub    → paler, less turquoise #F7FCFD
            //  .tn__r:hover unselected      → grey #F1F4F8 (distinct from inherited)
            backgroundColor: isRetained ? '#E6F7FB' : isInherited ? '#F7FCFD' : 'transparent',
            boxShadow: isRetained ? 'inset 0 0 0 1px #BFE6F1' : 'none',
            '&:hover': {
              backgroundColor: isRetained ? '#E6F7FB' : isInherited ? '#F7FCFD' : '#F1F4F8',
            },
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={() => toggleCollapse(node.id)}
              aria-expanded={isOpen}
              aria-label={
                isOpen
                  ? t('perimeterSelector.collapse', { name: label })
                  : t('perimeterSelector.expand', { name: label })
              }
            >
              {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 34 }} />
          )}
          <Checkbox
            size="small"
            checked={isCovered}
            indeterminate={isIndeterminate}
            onChange={() => toggle(node.id)}
            // MUI's `indeterminate` only changes the icon; with checked={false} a
            // partially covered country would be announced "unchecked". Expose the
            // tri-state to assistive tech via aria-checked="mixed".
            inputProps={{
              'aria-label': label,
              'aria-checked': isIndeterminate ? 'mixed' : isCovered,
            }}
            sx={isInherited ? { opacity: 0.55 } : undefined}
          />
          <Typography sx={{ flex: 1, color: 'text.body', fontSize: '0.875rem' }}>
            {label}
          </Typography>
          {isRetained && (
            <Chip
              size="small"
              label={t('perimeterSelector.selected')}
              // Maquette .tn__inh : pale-turquoise pill, teal text.
              sx={{ bgcolor: '#E6F7FB', color: '#0E7490', fontWeight: 600 }}
            />
          )}
          {isInherited && (
            <Chip
              size="small"
              label={t('perimeterSelector.inherited')}
              // Same maquette .tn__inh pill as « Sélectionné » : accent-light + teal.
              sx={{ bgcolor: '#E6F7FB', color: '#0E7490', fontWeight: 600 }}
            />
          )}
        </Box>
        {hasChildren && isOpen && node.children.map(child => renderNode(child, depth + 1))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: 440,
        maxWidth: '90vw',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography sx={{ flex: 1, color: 'text.body', fontWeight: 600 }}>
          {t('perimeterSelector.title')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('perimeterSelector.close')}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t('perimeterSelector.searchPlaceholder')}
          sx={{ '& .MuiInputBase-input': { color: 'text.body' } }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>{renderNode(tree.root, 0)}</Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography sx={{ flex: 1, color: 'text.body', fontSize: '0.8125rem' }}>
          {t('perimeterSelector.footer', {
            selected: selectedCount,
            selectedLabel: t('perimeterSelector.selectedLabel', { count: selectedCount }),
            covered: terrainCoverageCount(draft, tree),
          })}
        </Typography>
        <Button variant="text" onClick={onClose}>
          {t('perimeterSelector.cancel')}
        </Button>
        <Button variant="contained" onClick={apply}>
          {t('perimeterSelector.apply')}
        </Button>
      </Box>
    </Box>
  );
}
